"use client";

import React, { useState, useEffect, useContext, useRef } from "react";
import { useRouter } from "next/navigation";
import { AppRouterContext } from "next/dist/shared/lib/app-router-context.shared-runtime";
import { useLanguageVariables } from "@/contexts/LanguageVariablesContext";
import { useCustomerAuth } from "@/contexts/CustomerAuthContext";
import { useCaptchaConfig } from "@/hooks/useCaptchaConfig";
import { CaptchaService } from "@/services/CaptchaService";
import { ErrorHandlerService } from "@/services/ErrorHandlerService";
import { motion, AnimatePresence } from "framer-motion";
import ForgotPasswordForm from "./ForgotPasswordForm";
import AuthCaptchaField from "./AuthCaptchaField";
import type { PageUrlMap } from "@/types/blog";
import { UrlBuilder } from "@/lib/url-builder";

const setFieldValidity = (
  el: HTMLInputElement,
  message: string,
) => {
  el.setCustomValidity(message);
};

function useSafeRouter() {
  const routerContext = useContext(AppRouterContext);
  try {
    return useRouter();
  } catch (error) {
    if (typeof window === 'undefined' || !routerContext) {
      return null;
    }
    throw error;
  }
}

interface SocialProvider {
  provider: string;
  isActive: boolean;
  name?: string;
}

interface LoginFormProps {
  language: string;
  translations?: Record<string, string>;
  pageUrls?: PageUrlMap;
  socialProviders?: SocialProvider[];
  defaultLanguage?: string;
  /** Form modelinden (customer-login); captcha zorunluluğu */
  requireCaptcha?: boolean;
}

class RedirectHandler {
  static extractReturnUrl(): string | null {
    if (typeof window === 'undefined') return null;

    const searchParams = new URLSearchParams(window.location.search);
    let returnUrl = searchParams.get("return");

    if (!returnUrl && window.location.hash) {
      try {
        const hashString = window.location.hash.substring(1);
        if (hashString.startsWith('?')) {
          const hashParams = new URLSearchParams(hashString.substring(1));
          returnUrl = hashParams.get("return");
        } else {
          const hashMatch = window.location.hash.match(/[#&]return=([^&]*)/);
          if (hashMatch) {
            returnUrl = decodeURIComponent(hashMatch[1]);
          }
        }
      } catch {
        // Hash parse hatası - returnUrl boş kalır
      }
    }

    if (window.location.hash) {
      window.history.replaceState(null, '', window.location.pathname + window.location.search);
    }

    return returnUrl;
  }

  static buildDashboardUrl(pageUrls: PageUrlMap | undefined, language: string, defaultLanguage: string): string | null {
    if (!pageUrls?.['customer-dashboard']) {
      return null;
    }

    const builder = new UrlBuilder(pageUrls, language, defaultLanguage);
    return builder.buildCustomerPanelUrl('customer-dashboard');
  }
}

export default function LoginForm({
  language,
  translations,
  pageUrls,
  socialProviders = [],
  defaultLanguage = 'en',
  requireCaptcha = true,
}: LoginFormProps) {
  const { getVariable } = useLanguageVariables();
  const { login: loginCustomer, isLoading: authLoading } = useCustomerAuth();
  const router = useSafeRouter();
  const [error, setError] = useState<string | null>(null);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    remember: false,
  });
  
  const captchaConfig = useCaptchaConfig(requireCaptcha);
  const captchaServiceRef = useRef<CaptchaService | null>(null);
  const showCaptchaWidget =
    requireCaptcha &&
    !captchaConfig.isLoading &&
    Boolean(captchaConfig.config?.is_enabled && captchaConfig.config?.site_key);

  const t = (key: string, fallback: string) => {
    return translations?.[key] || getVariable(key, fallback);
  };
  
  useEffect(() => {
    if (!requireCaptcha) return;
    if (captchaConfig.isLoading) return;
    if (!captchaConfig.config?.is_enabled || !captchaConfig.config?.site_key || !captchaConfig.config?.provider) {
      return;
    }
    
    let isMounted = true;
    let isInitialized = false;
    
    const initCaptcha = async () => {
      if (isInitialized || !isMounted) return;
      
      try {
        if (!captchaConfig.config?.provider || !captchaConfig.config?.site_key) return;
        
        if (captchaServiceRef.current) {
          try {
            captchaServiceRef.current.remove();
          } catch {
            // Ignore cleanup errors
          }
          captchaServiceRef.current = null;
        }
        
        const captchaService = new CaptchaService('login-captcha-widget-container');
        await captchaService.initialize({
          is_enabled: captchaConfig.config.is_enabled,
          provider: captchaConfig.config.provider,
          site_key: captchaConfig.config.site_key,
          script_url: captchaConfig.config.script_url || undefined,
        });
        
        if (isMounted) {
          captchaServiceRef.current = captchaService;
          isInitialized = true;
        } else {
          try {
            captchaService.remove();
          } catch {
            // Ignore cleanup errors
          }
        }
      } catch (error) {
        console.error('[LoginForm] Captcha initialization error:', error);
      }
    };
    
    initCaptcha();
    
    return () => {
      isMounted = false;
      isInitialized = false;
      if (captchaServiceRef.current) {
        try {
          captchaServiceRef.current.remove();
        } catch {
          // Ignore cleanup errors
        }
        captchaServiceRef.current = null;
      }
    };
  }, [requireCaptcha, captchaConfig.config, captchaConfig.isLoading]);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);

    const form = e.currentTarget;
    if (!form.checkValidity()) {
      form.reportValidity();
      return;
    }

    try {
      let finalCaptchaToken = '';
      const captchaNeeded =
        requireCaptcha &&
        !captchaConfig.isLoading &&
        Boolean(captchaConfig.config?.is_enabled && captchaConfig.config?.site_key);

      if (captchaNeeded) {
        if (!captchaServiceRef.current?.isEnabled()) {
          setError(t("auth.login.validation.captcha_required", "Please complete the verification."));
          return;
        }
        try {
          finalCaptchaToken = await captchaServiceRef.current.getToken('login');
          if (!finalCaptchaToken) {
            setError(t("auth.login.validation.captcha_required", "Please complete the verification."));
            captchaServiceRef.current.reset();
            return;
          }
        } catch (error) {
          console.error('[LoginForm] Captcha token error:', error);
          setError(t("auth.login.validation.captcha_required", "Please complete the verification."));
          return;
        }
      }
      
      await loginCustomer(formData.email, formData.password, formData.remember, language, finalCaptchaToken || undefined);
      await new Promise(resolve => setTimeout(resolve, 200));

      if (!router) return;

      const returnUrl = RedirectHandler.extractReturnUrl();
        
        if (returnUrl) {
          const decodedReturnUrl = decodeURIComponent(returnUrl);
          const finalUrl = decodedReturnUrl.startsWith('/') ? decodedReturnUrl : `/${decodedReturnUrl}`;
          router.push(finalUrl);
          return;
      }

      // Önce pageUrls prop'undan customer-dashboard URL'ini kontrol et
      let dashboardUrl: string | null = null;
          
      if (pageUrls && pageUrls['customer-dashboard']) {
        dashboardUrl = RedirectHandler.buildDashboardUrl(pageUrls, language, defaultLanguage);
      }
      
      // Eğer pageUrls prop'unda yoksa, ana sayfaya yönlendir
      // API'ye istek atmak yerine, PageRenderer'dan gelen pageUrls'i kullan
      if (!dashboardUrl || dashboardUrl === '#') {
            router.push('/');
            return;
          }
          
          router.push(dashboardUrl);
    } catch (err) {
      const cleanedMessage = ErrorHandlerService.cleanErrorMessage(err, t("auth.login.error.message", "Login failed"));
      setError(cleanedMessage);
    } finally {
      if (captchaServiceRef.current?.isEnabled()) {
        try {
          captchaServiceRef.current.reset();
        } catch {
          // reset hatası görmezden gel
        }
      }
    }
  };

  const handleSocialLogin = async (provider: string) => {
    try {
      const response = await fetch(`/api/customer/auth/social/${provider}/redirect`);
      const data = await response.json();

      if (data.success && data.data?.redirect_url) {
        window.location.href = data.data.redirect_url;
      } else {
        throw new Error(t("auth.social.provider_not_available", "Provider not available"));
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : t("auth.social.provider_not_available", "Provider not available"));
    }
  };

  const activeSocialProviders = socialProviders.filter((p) => p.isActive);

  const loginFormVariants = {
    hidden: { x: '-100%', opacity: 0 },
    visible: { x: 0, opacity: 1 },
    exit: { x: '-100%', opacity: 0 }
  };

  const forgotPasswordVariants = {
    hidden: { x: '100%', opacity: 0 },
    visible: { x: 0, opacity: 1 },
    exit: { x: '100%', opacity: 0 }
  };

  return (
    <div className="relative overflow-hidden">
      <AnimatePresence mode="wait">
        {!showForgotPassword ? (
          <motion.form
            key="login"
            onSubmit={handleSubmit}
            noValidate
            className="space-y-6"
            variants={loginFormVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            transition={{ type: "spring", stiffness: 300, damping: 30 }}
          >
      {error && (
        <div className="bg-red-50 border-l-4 border-red-500 text-red-700 px-4 py-3 rounded-r-md">
          <p className="text-sm font-medium">{error}</p>
        </div>
      )}

      <div className="flex flex-col gap-5">
        <label htmlFor="email" className="font-semibold text-base sm:text-lg text-alsero-dark-gray">
          {t("auth.login.form.email", "Email")}
          <span className="text-red-500 ml-1">*</span>
        </label>
        <motion.input
          type="email"
          id="email"
          name="email"
          autoComplete="email"
          value={formData.email}
          onChange={(e) => {
            setFieldValidity(e.currentTarget, '');
            setFormData({ ...formData, email: e.target.value });
          }}
          onInvalid={(e) => {
            const input = e.currentTarget;
            if (input.validity.valueMissing) {
              setFieldValidity(input, t("auth.login.validation.email_or_phone_required", "Email or phone number is required"));
            } else if (input.validity.typeMismatch) {
              setFieldValidity(input, t("auth.register.validation.email_invalid", "Invalid email format"));
            }
          }}
          required
          className="border border-[rgba(0,0,0,0.08)] focus:!border-[rgba(0,0,0,0.40)] transition-all ease-in-out duration-300 rounded-lg text-[#4F545A] font-medium text-sm sm:text-[15px] px-4 sm:px-6 py-3 sm:py-4 w-full"
          placeholder={t("auth.login.form.email_placeholder", "Enter your email")}
          whileHover={{ scale: 1.005, borderColor: "rgba(0, 0, 0, 0.15)" }}
          whileFocus={{ scale: 1.01, borderColor: "rgba(227, 30, 36, 0.3)" }}
          transition={{ type: "spring" as const, stiffness: 300, damping: 20 }}
        />
      </div>

      <div className="flex flex-col gap-5">
        <label htmlFor="password" className="font-semibold text-base sm:text-lg text-alsero-dark-gray">
          {t("auth.login.form.password", "Password")}
          <span className="text-red-500 ml-1">*</span>
        </label>
        <div className="relative">
          <motion.input
            type="password"
            id="password"
            name="password"
            autoComplete="current-password"
            value={formData.password}
            onChange={(e) => {
              setFieldValidity(e.currentTarget, '');
              setFormData({ ...formData, password: e.target.value });
            }}
            onInvalid={(e) => {
              setFieldValidity(
                e.currentTarget,
                t("auth.login.validation.password_required", "Password is required"),
              );
            }}
            required
            className="border border-[rgba(0,0,0,0.08)] focus:!border-[rgba(0,0,0,0.40)] transition-all ease-in-out duration-300 rounded-lg text-[#4F545A] font-medium text-sm sm:text-[15px] px-4 sm:px-6 py-3 sm:py-4 w-full pr-10"
            placeholder={t("auth.login.form.password_placeholder", "Enter your password")}
            whileHover={{ scale: 1.005, borderColor: "rgba(0, 0, 0, 0.15)" }}
            whileFocus={{ scale: 1.01, borderColor: "rgba(227, 30, 36, 0.3)" }}
            transition={{ type: "spring" as const, stiffness: 300, damping: 20 }}
          />
          <button
            type="button"
            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
            onClick={() => {
              const passwordInput = document.getElementById('password') as HTMLInputElement;
              if (passwordInput) {
                passwordInput.type = passwordInput.type === 'password' ? 'text' : 'password';
              }
            }}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
            </svg>
          </button>
        </div>
        <button
          type="button"
          onClick={() => setShowForgotPassword(true)}
          className="mt-2 block text-sm text-red-600 hover:text-red-700 transition-colors text-left"
        >
          {t("auth.login.form.forgot_password", "Forgot Password?")}
        </button>
      </div>

      <div className="flex items-center">
        <input
          id="remember"
          type="checkbox"
          checked={formData.remember}
          onChange={(e) => setFormData({ ...formData, remember: e.target.checked })}
          className="h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded"
        />
        <label htmlFor="remember" className="ml-2 block text-sm text-gray-700">
          {t("auth.login.form.remember_me", "Remember me")}
        </label>
      </div>
      
      <AuthCaptchaField
        containerId="login-captcha-widget-container"
        provider={captchaConfig.config?.provider}
        show={showCaptchaWidget}
        protectedLabel={t(
          "auth.login.captcha.protected",
          "This form is protected by reCAPTCHA. Your interaction is verified before sign-in.",
        )}
      />

      <button
        type="submit"
              disabled={authLoading || (requireCaptcha && captchaConfig.isLoading)}
        className="w-full bg-red-600 text-white py-3 px-4 rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium text-base"
      >
              {authLoading ? t("auth.login.form.submitting", "Logging in...") : t("auth.login.form.submit", "Login")}
      </button>

      {activeSocialProviders.length > 0 && (
        <>
          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-300"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-4 bg-white text-gray-500">
                {t("auth.login.form.or", "or")}
              </span>
            </div>
          </div>

          <div className="space-y-3">
            {activeSocialProviders.map((provider) => {
              const providerName = provider.provider.toLowerCase();
              const isGoogle = providerName === 'google';
              const isFacebook = providerName === 'facebook';
              
              return (
                <button
                  key={provider.provider}
                  type="button"
                  onClick={() => handleSocialLogin(provider.provider)}
                  className="w-full flex items-center justify-center px-4 py-3 border-2 border-gray-300 rounded-lg bg-white text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-red-500 transition-colors font-medium"
                >
                  {isGoogle && (
                    <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                    </svg>
                  )}
                  {isFacebook && (
                    <svg className="w-5 h-5 mr-2" fill="#1877F2" viewBox="0 0 24 24">
                      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                    </svg>
                  )}
                  {t(`auth.login.social.${provider.provider}`, provider.name || `Sign in with ${provider.provider}`)}
                </button>
              );
            })}
          </div>
        </>
      )}
    </motion.form>
        ) : (
          <motion.div
            key="forgot-password"
            variants={forgotPasswordVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            transition={{ type: "spring", stiffness: 300, damping: 30 }}
          >
            <ForgotPasswordForm
              language={language}
              translations={translations}
              onBack={() => setShowForgotPassword(false)}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
